import React from 'react';
import { LayoutIcon, BarChartIcon, FileTextIcon, ChevronLeftIcon, ChevronRightIcon, DashboardIcon, GearIcon, PersonIcon } from '@radix-ui/react-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface SidebarProps {
  activeItem: string;
  setActiveItem: (item: string) => void;
  collapsed: boolean;
  toggleCollapse: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeItem, setActiveItem, collapsed, toggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  
  // Define menu items based on user role
  const orgAdminMenuItems = [
    
    { id: 'documents', path: '/documents', icon: <FileTextIcon className="w-5 h-5" />, label: 'Documents' },
    { id: 'data', path: '/data', icon: <BarChartIcon className="w-5 h-5" />, label: 'Data' },
    { id: 'usage', path: '/usage', icon: <BarChartIcon className="w-5 h-5" />, label: 'Usage' },
  ];
  
  const superAdminMenuItems = [
    { id: 'dashboard', path: '/admin/dashboard', icon: <DashboardIcon className="w-5 h-5" />, label: 'Dashboard' },
    { id: 'organizations', path: '/admin/organizations', icon: <LayoutIcon className="w-5 h-5" />, label: 'Organizations' },
    { id: 'users', path: '/admin/users', icon: <PersonIcon className="w-5 h-5" />, label: 'Users' },
    { id: 'quota', path: '/admin/quota', icon: <GearIcon className="w-5 h-5" />, label: 'Quota' },
  ];
  
  // Select menu items based on user role
  const menuItems = currentUser?.role === 'SuperAdmin' ? superAdminMenuItems : orgAdminMenuItems;

  const handleNavigation = (item: { id: string, path: string }) => {
    setActiveItem(item.id);
    navigate(item.path);
  };

  // Determine active item based on current path
  const currentPath = location.pathname;
  const currentMenuItem = menuItems.find(item => {
    // Exact match
    if (currentPath === item.path) return true;
    // Root path defaults to documents
    if (currentPath === '/' && item.id === 'documents') return true;
    // Handle export-data path
    if (currentPath === '/export-data' && item.id === 'data') return true;
    return false;
  });

  return (
    <div className={`${collapsed ? 'w-[60px]' : 'w-[175px]'} bg-sidebar dark:bg-dark-sidebar h-full flex flex-col border-r border-gray-200 dark:border-dark-border transition-all duration-300 ease-in-out relative`}>
      <div className={`${collapsed ? 'justify-center h-[56px]' : 'p-4'} border-b border-gray-200 dark:border-dark-border flex items-center`}>
        {!collapsed && <div className="text-lg font-semibold text-center flex-1 dark:text-dark-text">MENU</div>}
      </div>
      <div className="flex flex-col p-2 space-y-1">
        {menuItems.map((item) => (
          <button
            key={item.id}
            className={`flex items-center ${collapsed ? 'justify-center p-2 h-[40px]' : 'p-3'} rounded-md ${
              currentMenuItem?.id === item.id
                ? 'bg-blue-50 dark:bg-gray-700 text-primary'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            onClick={() => handleNavigation(item)}
            title={collapsed ? item.label : undefined}
          >
            <div className={collapsed ? '' : 'mr-3'}>{item.icon}</div>
            {!collapsed && <span>{item.label}</span>}
          </button>
        ))}
      </div>
      <button 
        onClick={toggleCollapse}
        className="absolute -right-3 top-20 bg-white dark:bg-gray-700 rounded-full p-1 shadow-md border border-gray-200 dark:border-dark-border text-gray-500 dark:text-gray-300 hover:text-primary dark:hover:text-primary focus:outline-none"
      >
        {collapsed ? <ChevronRightIcon className="w-4 h-4" /> : <ChevronLeftIcon className="w-4 h-4" />}
      </button>
    </div>
  );
};

export default Sidebar;
